import { useContext } from "react";

import { SafeHtml } from "~/hec/components/atoms/safe-html/safe-html";
import { Heading } from "~/hec/components/ui/heading";
import { InsideLinkContext } from "~/hec/components/ui/inside-link-context";
import { useHeadingFontClass } from "~/hec/hooks/use-heading-font-class";
import { toHeadingHtml } from "~/hec/utils/rich-text";
import { cn } from "~/lib/utils";

import type { RichHeadingProps } from "./types";

// Zero-width space: one line box, no visible text.
const EDITOR_PLACEHOLDER = "&#8203;";

/**
 * One heading element from rich text (COM-884): each authored line is a `<br>`,
 * never a second heading. Links are kept unless the heading already sits
 * inside a link (e.g. a Heading child of a Button), where a nested `<a>` would
 * break the page. The font is the site's heading font (FSA Sans Serif,
 * HSA/WDH Serif); Builder's Style tab Typography font overrides it.
 * Renders nothing for empty content; in the Builder editor an empty heading
 * keeps one line of height so the author can still select it.
 */
export function RichHeading({
  tag,
  variant,
  content,
  inEditor,
  className,
  "data-testid": dataTestId,
  ...rest
}: RichHeadingProps) {
  const insideLink = useContext(InsideLinkContext);
  // Site heading font; an author's Style tab (Typography) font overrides it.
  const fontClass = useHeadingFontClass();
  const html = toHeadingHtml(content, { keepLinks: !insideLink });
  if (!html && !inEditor) return null;

  return (
    <Heading
      tag={tag}
      variant={variant}
      className={cn(fontClass, "[&_a]:hover:underline", className)}
      {...rest}
      data-testid={dataTestId}
    >
      <SafeHtml as="span" html={html || EDITOR_PLACEHOLDER} />
    </Heading>
  );
}
